import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import {
  ArrowClockwise,
  CloudUpload,
  ExclamationTriangleFill,
  ArrowLeft,
  Check2Circle,
  X,
} from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { PusherContext } from '../../providers/PusherProvider';
import { PRO_GOLD } from '../billing/billingPlanData';

const MODES = {
  rebuild: 'rebuild',
  upload: 'upload',
  deep: 'deep',
};

const STEPS = {
  choose: 'choose',
  configure: 'configure',
  running: 'running',
  result: 'result',
};

const REBUILD_GREEN = '#22c55e';
const UPLOAD_BLUE = '#3B82F6';
const DEEP_GOLD = PRO_GOLD;

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { error: text || 'Request failed' };
  }
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StepHeading({ icon, iconColor, heading, subtitle, colors }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 36 }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          background: `${iconColor}18`,
          border: `1px solid ${iconColor}55`,
        }}
      >
        {icon}
      </div>
      <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: colors.text, margin: subtitle ? '0 0 8px' : 0 }}>
        {heading}
      </h2>
      {subtitle && (
        <p style={{ color: colors.textMuted, fontSize: 13, margin: 0, lineHeight: 1.45 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function ModeOption({ selected, onSelect, icon, title, description, accent, colors, disabled }) {
  const accentColor = accent || colors.primary;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        width: '100%',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '14px 16px',
        marginBottom: 10,
        borderRadius: 12,
        border: `1.5px solid ${selected ? accentColor : colors.border}`,
        background: selected ? `${accentColor}12` : (colors.bgPrimary || colors.bgCard),
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        boxShadow: selected ? `0 0 0 1px ${accentColor}33` : 'none',
        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${accentColor}22`,
          color: accentColor,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 650, fontSize: '0.95rem', color: colors.text, marginBottom: 3 }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: colors.textMuted, lineHeight: 1.45 }}>
          {description}
        </div>
      </div>
      <div
        aria-hidden
        style={{
          flexShrink: 0,
          width: 18,
          height: 18,
          marginTop: 2,
          borderRadius: '50%',
          border: `2px solid ${selected ? accentColor : colors.border}`,
          background: selected ? accentColor : 'transparent',
          boxShadow: selected ? `inset 0 0 0 3px ${colors.bgPrimary || colors.bgCard}` : 'none',
        }}
      />
    </button>
  );
}

function StatTile({ label, value, colors, accent, sub }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 90,
        padding: '12px 12px',
        borderRadius: 10,
        backgroundColor: colors.bgPrimary || colors.bgCard,
        border: `1px solid ${accent ? `${accent}55` : colors.border}`,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: accent || colors.text, lineHeight: 1.2 }}>
        {value}
      </div>
      <div style={{ fontSize: '0.7rem', color: colors.textMuted, marginTop: 3 }}>{label}</div>
      {sub != null && sub !== '' && (
        <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginTop: 4, lineHeight: 1.3 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function LedgerStatsCard({ title, summary, colors, loading }) {
  return (
    <div
      style={{
        padding: '14px 14px 12px',
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        background: colors.bgPrimary || colors.bgCard,
        marginBottom: 8,
      }}
    >
      {title && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 650,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            marginBottom: 10,
          }}
        >
          {title}
        </div>
      )}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
          <Spinner size="sm" animation="border" />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <StatTile
            label="Order events"
            value={summary?.orderEventCount ?? 0}
            colors={colors}
          />
          <StatTile
            label="Trades"
            value={summary?.tradeCount ?? 0}
            colors={colors}
          />
          <StatTile
            label="Oldest order"
            value={summary?.oldestOrderAt ? formatTimestamp(summary.oldestOrderAt).split(',')[0] : '—'}
            sub={summary?.oldestOrderAt ? formatTimestamp(summary.oldestOrderAt).split(',').slice(1).join(',').trim() : 'No events yet'}
            colors={colors}
          />
        </div>
      )}
    </div>
  );
}

function getUploadInstructions(brokerKey) {
  if (brokerKey === 'WEBULL') {
    return {
      title: 'How to export from Webull',
      body: 'In the Webull app or desktop app, open the Orders table, set the date filter to cover about two years of history, then download those orders as CSV.',
    };
  }
  if (brokerKey === 'TRADESTATION' || !brokerKey) {
    return {
      title: 'How to export from TradeStation',
      body: (
        <>
          <ol style={{ margin: '0 0 8px', paddingLeft: 18 }}>
            <li>Open TradeStation → <strong>Portfolio</strong> in the sidebar</li>
            <li>Open the <strong>Activity</strong> tab</li>
            <li>Set the date range as far back as possible</li>
            <li>Click <strong>Download CSV</strong></li>
          </ol>
          You can rearrange or hide columns in TradeStation — we match by header name
          (Symbol, Side, Quantity, Price, Order ID, etc.), not column order. The report
          title rows above the header are ignored automatically.
        </>
      ),
    };
  }
  return {
    title: `How to export from ${brokerKey}`,
    body: 'Upload an orders CSV from your broker. Prefer the widest date range the export allows.',
  };
}

export default function PerformanceSyncModal({
  show,
  onHide,
  accountId,
  isPaperTrading,
  authToken,
  backendUrl,
  showMessage,
  onSuccess,
  brokerName,
  trackJob,
  activeSyncJob,
}) {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext) || {};
  const pusher = useContext(PusherContext);
  const fileInputRef = useRef(null);
  const activeJobIdRef = useRef(null);
  const showRef = useRef(show);
  showRef.current = show;
  const hasSeenActiveJobRef = useRef(false);
  // Parent monitor owns completion toasts when trackJob is provided.
  const parentTracksJobs = typeof trackJob === 'function';

  const [step, setStep] = useState(STEPS.choose);
  const [mode, setMode] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [deepConfirmText, setDeepConfirmText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  // True once the user has confirmed a force-deep-rebuild and is now on the
  // upload step to supply the data the deep rebuild should replay from.
  const [deepRebuildPending, setDeepRebuildPending] = useState(false);

  const brokerKey = String(brokerName || '').toUpperCase();
  const uploadInstructions = getUploadInstructions(brokerKey);

  const accountLabel = accountId || '—';
  const deepConfirmOk = !!accountId && deepConfirmText.trim() === String(accountId);

  const resetState = useCallback(() => {
    setStep(STEPS.choose);
    setMode(null);
    setCsvFile(null);
    setDeepConfirmText('');
    setDragOver(false);
    setLoading(false);
    setSyncResult(null);
    setLedgerSummary(null);
    setLedgerLoading(false);
    setJobStatus(null);
    setDeepRebuildPending(false);
    activeJobIdRef.current = null;
    hasSeenActiveJobRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    // Block dismiss only while a request is in-flight on the configure step.
    // The running step is a background job — users can close and keep working.
    if (loading && step !== STEPS.running) return;
    if (step === STEPS.running) {
      // Never clear loading / job id while a sync is in flight (even before jobId arrives).
      onHide();
      return;
    }
    resetState();
    onHide();
  }, [loading, onHide, resetState, step]);

  useEffect(() => {
    if (show) return;
    // Keep job listeners alive if the user dismissed the running step.
    if (loading || activeJobIdRef.current) return;
    resetState();
  }, [show, loading, resetState]);

  const finishJobSuccess = useCallback((result, jobMode) => {
    setLoading(false);
    setJobStatus('completed');
    activeJobIdRef.current = null;
    hasSeenActiveJobRef.current = false;
    if (!parentTracksJobs) {
      showMessage?.(
        jobMode === MODES.deep || result?.forceDeepRebuild
          ? 'Deep rebuild complete'
          : jobMode === MODES.upload
            ? 'Upload succeeded'
            : 'Rebuild complete',
        'success',
        false,
      );
      onSuccess?.();
    }
    if (showRef.current) {
      setSyncResult(result);
      setStep(STEPS.result);
    } else {
      resetState();
    }
  }, [onSuccess, parentTracksJobs, resetState, showMessage]);

  const finishJobFailure = useCallback((message) => {
    setLoading(false);
    setJobStatus('failed');
    activeJobIdRef.current = null;
    hasSeenActiveJobRef.current = false;
    if (!parentTracksJobs) {
      showMessage?.(message || 'Sync failed', 'danger', false);
    }
    if (showRef.current) {
      setStep(STEPS.configure);
    } else {
      resetState();
    }
  }, [parentTracksJobs, resetState, showMessage]);

  // Reflect parent monitor's job id/status into modal UI, and fetch the final
  // result ourselves once the parent clears the job (it only toasts + drops
  // the payload). We only treat a cleared job as "settled" once we've
  // actually seen it tracked — the parent hook can briefly report no active
  // job right as one is being started (before its own state update lands),
  // which previously caused the modal to snap back to the picker instead of
  // showing the running state at all.
  useEffect(() => {
    if (!parentTracksJobs) return undefined;
    if (activeSyncJob?.jobId) {
      activeJobIdRef.current = activeSyncJob.jobId;
      hasSeenActiveJobRef.current = true;
      if (loading) setJobStatus((s) => s || 'running');
      return undefined;
    }
    if (!loading || step !== STEPS.running || !hasSeenActiveJobRef.current) return undefined;
    hasSeenActiveJobRef.current = false;

    const jobId = activeJobIdRef.current;
    const fallbackToChoose = () => {
      setLoading(false);
      setJobStatus(null);
      activeJobIdRef.current = null;
      if (showRef.current) {
        setStep(STEPS.choose);
        setMode(null);
      } else {
        resetState();
      }
    };

    if (!jobId || !authToken || !backendUrl) {
      fallbackToChoose();
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${backendUrl}/performance/trades/sync-trade-history/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const data = await parseJsonResponse(res);
        if (cancelled) return;
        if (res.ok && data.status === 'completed' && data.result) {
          finishJobSuccess(data.result, mode);
        } else if (res.ok && data.status === 'failed') {
          finishJobFailure(data.error);
        } else {
          fallbackToChoose();
        }
      } catch (_) {
        if (!cancelled) fallbackToChoose();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSyncJob, authToken, backendUrl, finishJobFailure, finishJobSuccess, loading, mode, parentTracksJobs, resetState, step]);

  useEffect(() => {
    if (parentTracksJobs) return undefined; // parent owns Pusher + poll toasts
    if (!pusher || !user?.id) return undefined;
    const channelName = `user-${user.id}-alerts`;
    let channel = pusher.channel(channelName);
    if (!channel) channel = pusher.subscribe(channelName);

    const onSync = (data) => {
      if (!data?.jobId || data.jobId !== activeJobIdRef.current) return;
      if (data.status === 'running') {
        setJobStatus('running');
        return;
      }
      if (data.status === 'completed' && data.result) {
        finishJobSuccess(data.result, mode);
        return;
      }
      if (data.status === 'failed') {
        finishJobFailure(data.error);
      }
    };
    channel.bind('trade-history-sync', onSync);
    return () => {
      channel.unbind('trade-history-sync', onSync);
    };
  }, [pusher, user?.id, mode, finishJobSuccess, finishJobFailure, parentTracksJobs]);

  useEffect(() => {
    if (parentTracksJobs) return undefined; // parent owns Pusher + poll toasts
    if (!loading || !activeJobIdRef.current || !authToken || !backendUrl) return undefined;
    let cancelled = false;
    const tick = async () => {
      const jobId = activeJobIdRef.current;
      if (!jobId || cancelled) return;
      try {
        const res = await fetch(
          `${backendUrl}/performance/trades/sync-trade-history/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const data = await parseJsonResponse(res);
        if (cancelled || jobId !== activeJobIdRef.current) return;
        if (!res.ok) return;
        if (data.status === 'completed' && data.result) {
          finishJobSuccess(data.result, mode);
        } else if (data.status === 'failed') {
          finishJobFailure(data.error);
        } else {
          setJobStatus(data.status || 'running');
        }
      } catch (_) {
        /* ignore */
      }
    };
    const t = setInterval(tick, 2500);
    tick();
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [loading, authToken, backendUrl, mode, finishJobSuccess, finishJobFailure, parentTracksJobs]);

  const fetchLedgerSummary = useCallback(async () => {
    if (!accountId || !authToken || !backendUrl) return;
    setLedgerLoading(true);
    try {
      const qs = new URLSearchParams({
        accountId: String(accountId),
        paperTrading: String(!!isPaperTrading),
      });
      const res = await fetch(`${backendUrl}/performance/trades/ledger-summary?${qs}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(data.error || 'Failed to load ledger summary');
      setLedgerSummary(data);
    } catch (err) {
      showMessage?.(err?.message || 'Failed to load ledger summary', 'warning');
      setLedgerSummary(null);
    } finally {
      setLedgerLoading(false);
    }
  }, [accountId, authToken, backendUrl, isPaperTrading, showMessage]);

  const acceptFile = useCallback(
    (file) => {
      if (!file) return;
      const name = (file.name || '').toLowerCase();
      if (name.endsWith('.pdf')) {
        showMessage?.(
          'PDF tax forms are not supported — export Orders Records as CSV from Webull, or Portfolio → Activity CSV from TradeStation.',
          'warning'
        );
        return;
      }
      if (!name.endsWith('.csv')) {
        showMessage?.('Please upload a .csv file', 'warning');
        return;
      }
      setCsvFile(file);
    },
    [showMessage]
  );

  const clearFile = useCallback(() => {
    setCsvFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const goConfigure = useCallback((nextMode) => {
    setMode(nextMode);
    setStep(STEPS.configure);
    setSyncResult(null);
    setDeepConfirmText('');
    setDeepRebuildPending(false);
    if (nextMode !== MODES.upload) clearFile();
    if (nextMode === MODES.rebuild) {
      fetchLedgerSummary();
    }
  }, [clearFile, fetchLedgerSummary]);

  const goBack = useCallback(() => {
    if (loading) return;
    // Coming from the deep-rebuild confirm step via "upload data instead" —
    // go back to the deep confirm step (keeping the typed confirmation),
    // not all the way to the mode picker.
    if (mode === MODES.upload && deepRebuildPending) {
      setMode(MODES.deep);
      setDeepRebuildPending(false);
      clearFile();
      return;
    }
    setStep(STEPS.choose);
    setMode(null);
    setDeepConfirmText('');
    setDeepRebuildPending(false);
    setLedgerSummary(null);
    clearFile();
  }, [clearFile, deepRebuildPending, loading, mode]);

  // After confirming a force deep rebuild, send the user to the upload step
  // so they can supply the CSV the rebuild should replay from.
  const proceedToUploadForDeepRebuild = useCallback(() => {
    if (!deepConfirmOk) return;
    setDeepRebuildPending(true);
    setMode(MODES.upload);
    setSyncResult(null);
    clearFile();
  }, [clearFile, deepConfirmOk]);

  const canRun = useMemo(() => {
    if (!accountId || loading) return false;
    if (mode === MODES.upload) return !!csvFile;
    if (mode === MODES.deep) return deepConfirmOk;
    return mode === MODES.rebuild;
  }, [accountId, csvFile, deepConfirmOk, loading, mode]);

  const handleRun = useCallback(async () => {
    if (!canRun) return;
    setLoading(true);
    setSyncResult(null);
    setJobStatus('queued');
    setStep(STEPS.running);

    try {
      const qs = new URLSearchParams({
        accountId: String(accountId),
        paperTrading: String(!!isPaperTrading),
      });
      if (brokerName) qs.set('brokerName', String(brokerName).toUpperCase());

      const formData = new FormData();
      if (brokerName) formData.append('brokerName', String(brokerName).toUpperCase());
      if (mode === MODES.deep || (mode === MODES.upload && deepRebuildPending)) {
        formData.append('forceDeepRebuild', 'true');
      }
      if (mode === MODES.upload && csvFile) formData.append('ordersCsv', csvFile);

      const res = await fetch(
        `${backendUrl}/performance/trades/sync-trade-history?${qs.toString()}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        }
      );

      const result = await parseJsonResponse(res);
      if (!res.ok) {
        setLoading(false);
        setJobStatus('failed');
        showMessage?.(result.message || result.error || 'Sync failed', 'danger', false);
        if (showRef.current) setStep(STEPS.configure);
        else resetState();
        return;
      }

      if (result.async && result.jobId) {
        activeJobIdRef.current = result.jobId;
        setJobStatus(result.status || 'queued');
        trackJob?.({
          jobId: result.jobId,
          mode,
          accountId,
        });
        return;
      }

      finishJobSuccess(result, mode);
    } catch (err) {
      setLoading(false);
      setJobStatus('failed');
      showMessage?.(err?.message || 'Sync failed', 'danger', false);
      if (showRef.current) setStep(STEPS.configure);
      else resetState();
    }
  }, [
    accountId,
    authToken,
    backendUrl,
    brokerName,
    canRun,
    csvFile,
    deepRebuildPending,
    finishJobSuccess,
    isPaperTrading,
    mode,
    resetState,
    showMessage,
    trackJob,
  ]);

  const dropZoneStyle = {
    border: `2px dashed ${dragOver ? UPLOAD_BLUE : colors.border}`,
    borderRadius: 12,
    padding: '28px 16px',
    textAlign: 'center',
    backgroundColor: dragOver ? `${UPLOAD_BLUE}11` : (colors.bgPrimary || colors.bgCard),
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
  };

  const footerBtn = {
    borderRadius: 10,
    fontWeight: 600,
    minHeight: 40,
  };

  const configureCopy = {
    [MODES.rebuild]: {
      heading: 'Rebuild trades',
      subtitle: `Pull the deepest broker history we can for ${accountLabel}, merge with anything already stored, then re-derive trades. Order events are kept — only the trades are rebuilt.`,
      cta: 'Rebuild',
      accent: REBUILD_GREEN,
    },
    [MODES.upload]: deepRebuildPending
      ? {
          heading: 'Force deep rebuild — upload data',
          subtitle: `We’ll delete existing history for ${accountLabel} and rebuild entirely from this file (plus anything we can still fetch from the broker).`,
          cta: 'Delete & rebuild from upload',
          accent: DEEP_GOLD,
        }
      : {
          heading: 'Upload more data',
          subtitle: null,
          cta: 'Upload and Rebuild',
          accent: UPLOAD_BLUE,
        },
    [MODES.deep]: {
      heading: 'Force deep rebuild',
      subtitle: `Start fresh for ${accountLabel} only.`,
      cta: 'Continue to upload',
      accent: DEEP_GOLD,
    },
  }[mode] || {};

  const resultSummary = syncResult?.after || null;
  const issues = Array.isArray(syncResult?.issues) ? syncResult.issues : [];
  const canDismiss = !loading || step === STEPS.running;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName="connect-broker-modal performance-sync-wizard"
      backdrop={canDismiss ? true : 'static'}
      keyboard={canDismiss}
    >
      <Modal.Body
        style={{
          background: colors.bgSecondary,
          padding: '40px 24px 28px',
          position: 'relative',
        }}
      >
        {canDismiss && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 14,
              right: 14,
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              padding: 6,
              cursor: 'pointer',
              zIndex: 1,
            }}
          >
            <X size={22} />
          </button>
        )}

        {step === STEPS.choose && (
          <>
            <StepHeading
              colors={colors}
              icon={<ArrowClockwise size={20} style={{ color: colors.primary }} aria-hidden />}
              iconColor={colors.primary}
              heading="Sync trade history"
              subtitle={
                <>
                  Choose how to update <strong style={{ color: colors.text }}>{accountLabel}</strong>
                  {isPaperTrading ? ' (paper)' : ''}. Only this account is affected.
                </>
              }
            />

            <ModeOption
              colors={colors}
              accent={REBUILD_GREEN}
              selected={mode === MODES.rebuild}
              onSelect={() => goConfigure(MODES.rebuild)}
              icon={<ArrowClockwise size={18} />}
              title="Rebuild"
              description="Re-run derivation from every order we’ve tracked, broker history, and prior uploads. Safe to repeat."
            />
            <ModeOption
              colors={colors}
              accent={UPLOAD_BLUE}
              selected={mode === MODES.upload}
              onSelect={() => goConfigure(MODES.upload)}
              icon={<CloudUpload size={18} />}
              title="Upload more data"
              description="Import a broker CSV. Already-seen fills are not duplicated — no harm in re-uploading."
            />
            <ModeOption
              colors={colors}
              accent={DEEP_GOLD}
              selected={mode === MODES.deep}
              onSelect={() => goConfigure(MODES.deep)}
              icon={<ExclamationTriangleFill size={18} />}
              title="Force deep rebuild"
              description="Last resort for this account when history looks badly wrong."
            />
          </>
        )}

        {step === STEPS.configure && mode && (
          <>
            <button
              type="button"
              onClick={goBack}
              disabled={loading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                fontSize: 13,
                padding: 0,
                marginBottom: 8,
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              <ArrowLeft size={14} /> Back
            </button>

            <StepHeading
              colors={colors}
              icon={
                mode === MODES.deep || (mode === MODES.upload && deepRebuildPending) ? (
                  <ExclamationTriangleFill size={20} style={{ color: DEEP_GOLD }} aria-hidden />
                ) : mode === MODES.upload ? (
                  <CloudUpload size={20} style={{ color: UPLOAD_BLUE }} aria-hidden />
                ) : (
                  <ArrowClockwise size={20} style={{ color: REBUILD_GREEN }} aria-hidden />
                )
              }
              iconColor={configureCopy.accent}
              heading={configureCopy.heading}
              subtitle={configureCopy.subtitle}
            />

            {mode === MODES.upload && (
              <>
                {deepRebuildPending && (
                  <div
                    style={{
                      padding: '12px 14px',
                      borderRadius: 12,
                      border: `1px solid ${DEEP_GOLD}66`,
                      background: `${DEEP_GOLD}14`,
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 650, color: colors.text, marginBottom: 4 }}>
                      Irreversible for {accountLabel}
                    </div>
                    <div style={{ fontSize: 12.5, color: colors.textMuted, lineHeight: 1.5 }}>
                      Existing history for this account will be deleted first, then everything will be
                      rebuilt from the file you upload here. We’ll also pull whatever order history the
                      broker will still give us, but brokers usually only keep the last few months
                      available — the file you upload is what fills in anything older than that.
                    </div>
                  </div>
                )}
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: `1px solid ${UPLOAD_BLUE}55`,
                    background: `${UPLOAD_BLUE}12`,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 650, color: colors.text, marginBottom: 4 }}>
                    {uploadInstructions.title}
                  </div>
                  <div style={{ fontSize: 12.5, color: colors.textMuted, lineHeight: 1.5 }}>
                    {uploadInstructions.body}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div
                    role="button"
                    tabIndex={0}
                    style={dropZoneStyle}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      acceptFile(e.dataTransfer.files?.[0]);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                    }}
                  >
                    <CloudUpload size={28} style={{ color: UPLOAD_BLUE, marginBottom: 8 }} />
                    <div style={{ fontSize: '0.9rem', color: colors.text, fontWeight: 550 }}>
                      Drag & drop CSV
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: 'none' }}
                      onChange={(e) => acceptFile(e.target.files?.[0])}
                    />
                  </div>
                  {csvFile && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        backgroundColor: colors.bgPrimary || colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        fontSize: '0.85rem',
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: colors.text,
                        }}
                      >
                        {csvFile.name}
                      </span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={clearFile}
                        style={{ color: colors.textMuted, padding: 0 }}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  )}
                  <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 12, marginBottom: 0, lineHeight: 1.45 }}>
                    Upload adds any missed orders and then rebuilds the trades. Duplicate orders are ignored.
                  </p>
                </div>
              </>
            )}

            {mode === MODES.rebuild && (
              <LedgerStatsCard
                title="Currently stored"
                summary={ledgerSummary}
                colors={colors}
                loading={ledgerLoading}
              />
            )}

            {mode === MODES.deep && (
              <div
                style={{
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: `1px solid ${DEEP_GOLD}66`,
                  background: `${DEEP_GOLD}14`,
                  marginBottom: 16,
                }}
              >
                <div style={{ fontSize: 13, color: colors.text, fontWeight: 600, marginBottom: 6 }}>
                  Irreversible for this account
                </div>
                <div style={{ fontSize: 12.5, color: colors.textMuted, lineHeight: 1.5, marginBottom: 12 }}>
                  Other accounts stay intact. If the trades don’t look correct, it’s usually better to{' '}
                  <strong style={{ color: colors.text }}>upload more data</strong> than to delete all events —
                  brokers often won’t let us retrieve every historical order again, so you may remove orders we
                  can’t get back.
                </div>
                <Form.Label style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>
                  Type <code style={{ color: colors.text }}>{accountLabel}</code> to continue
                </Form.Label>
                <Form.Control
                  value={deepConfirmText}
                  onChange={(e) => setDeepConfirmText(e.target.value)}
                  placeholder={accountLabel}
                  disabled={loading}
                  autoComplete="off"
                  style={{
                    background: colors.bgInput || colors.bgCard,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 10,
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button
                variant="outline-light"
                onClick={goBack}
                disabled={loading}
                style={{
                  ...footerBtn,
                  flex: 1,
                  borderColor: colors.border,
                  color: colors.text,
                  background: 'transparent',
                }}
              >
                Back
              </Button>
              <Button
                onClick={mode === MODES.deep ? proceedToUploadForDeepRebuild : handleRun}
                disabled={!canRun}
                style={{
                  ...footerBtn,
                  flex: 1.4,
                  border: 'none',
                  background: configureCopy.accent || colors.primary,
                  color: mode === MODES.deep || (mode === MODES.upload && deepRebuildPending) ? '#1a1206' : '#fff',
                  opacity: canRun ? 1 : 0.5,
                }}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Working…
                  </>
                ) : (
                  configureCopy.cta
                )}
              </Button>
            </div>
            {mode === MODES.deep && (
              <button
                type="button"
                onClick={handleRun}
                disabled={!canRun}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'center',
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  fontSize: 12,
                  marginTop: 12,
                  padding: 0,
                  cursor: canRun ? 'pointer' : 'default',
                  opacity: canRun ? 1 : 0.5,
                  textDecoration: 'underline',
                }}
              >
                Skip upload, rebuild from broker history now
              </button>
            )}
          </>
        )}

        {step === STEPS.running && (
          <>
            <StepHeading
              colors={colors}
              icon={<Spinner animation="border" size="sm" style={{ color: colors.primary }} />}
              iconColor={colors.primary}
              heading="Rebuilding trades…"
              subtitle={
                <>
                  Running in the background for <strong style={{ color: colors.text }}>{accountLabel}</strong>
                  {jobStatus ? ` · ${jobStatus}` : ''}. You can close this — we’ll notify you when it finishes.
                </>
              }
            />
            <p style={{ color: colors.textMuted, fontSize: 13, textAlign: 'center', margin: '0 0 20px' }}>
              Syncing broker history, applying splits, and re-deriving trades. This can take a few minutes on large accounts.
            </p>
            <Button
              onClick={handleClose}
              style={{
                ...footerBtn,
                width: '100%',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
              }}
            >
              Close
            </Button>
          </>
        )}

        {step === STEPS.result && syncResult && (
          <>
            <StepHeading
              colors={colors}
              icon={<Check2Circle size={22} style={{ color: REBUILD_GREEN }} aria-hidden />}
              iconColor={REBUILD_GREEN}
              heading="Done"
              subtitle={`Updated ${accountLabel}${syncResult.forceDeepRebuild ? ' with a deep rebuild' : ''}.`}
            />

            <LedgerStatsCard
              title="After sync"
              summary={resultSummary}
              colors={colors}
              loading={false}
            />

            {(syncResult.before || syncResult.rawOrderCount != null || syncResult.durationMs != null) && (
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  flexWrap: 'wrap',
                  fontSize: 12,
                  color: colors.textMuted,
                  marginTop: 4,
                  marginBottom: 8,
                }}
              >
                {syncResult.before && (
                  <span>
                    Was{' '}
                    <strong style={{ color: colors.text }}>{syncResult.before.orderEventCount ?? 0}</strong> events /{' '}
                    <strong style={{ color: colors.text }}>{syncResult.before.tradeCount ?? 0}</strong> trades
                  </span>
                )}
                {syncResult.rawOrderCount != null && (
                  <span>
                    Broker scan{' '}
                    <strong style={{ color: colors.text }}>{syncResult.rawOrderCount}</strong>
                  </span>
                )}
                {syncResult.csvFillCount > 0 && (
                  <span>
                    <strong style={{ color: colors.text }}>{syncResult.csvFillCount}</strong> CSV fills
                  </span>
                )}
                {syncResult.dedupedCount > 0 && (
                  <span>
                    <strong style={{ color: colors.text }}>{syncResult.dedupedCount}</strong> duplicates skipped
                  </span>
                )}
                {syncResult.durationMs != null && (
                  <span style={{ marginLeft: 'auto' }}>{(syncResult.durationMs / 1000).toFixed(1)}s</span>
                )}
              </div>
            )}

            {issues.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  padding: 14,
                  borderRadius: 12,
                  border: `1px solid ${DEEP_GOLD}66`,
                  background: `${DEEP_GOLD}12`,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  maxHeight: 'min(42vh, 280px)',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 650, color: colors.text, marginBottom: 8, flex: '0 0 auto' }}>
                  Couldn’t fully explain ({issues.length})
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    color: colors.textMuted,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                    minHeight: 0,
                  }}
                >
                  {issues.map((issue, idx) => (
                    <li key={`${issue.type}-${issue.symbol || idx}-${idx}`} style={{ marginBottom: 4 }}>
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(syncResult.incompleteCount ?? 0) > 0 && issues.length === 0 && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: colors.textMuted }}>
                {syncResult.incompleteCount} trade(s) marked incomplete during rebuild.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
              <Button
                onClick={handleClose}
                style={{
                  ...footerBtn,
                  flex: 1,
                  border: 'none',
                  background: colors.primary,
                  color: '#fff',
                }}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
